import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  capitalize,
  innerItemEditStyles,
  Log,
  requiredRuleStringField,
  TrafficTypeEnum,
  useFormats,
} from "@ryde/shared";

import {
  ColorIconButton,
  DropdownInputByControl,
  MultiSelectBoundInputByControl,
} from "../../../controls";
import { TimeTemplate } from "../time.template";
import {
  availabilityFormFieldNames,
  emptyAvailability,
  IAvailabilityItem,
  IAvailabilityItemForm,
} from "./traffic-availability.models";
import { useAvailabilityItemResolver } from "./use-availability-item-validation";

export const AvailabilityItemEdit: FC<IAvailabilityItemForm> = ({
  value,
  onCancelOuter,
  setValueOuter,
  fieldName,
}) => {
  const { t } = useTranslation();
  const { timeFormat } = useFormats();
  const { availabilityInnerFormValidationSchema } =
    useAvailabilityItemResolver();
  const methods = useForm<Partial<IAvailabilityItem>>({
    defaultValues: value || emptyAvailability,
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(availabilityInnerFormValidationSchema),
  });

  const {
    trigger,
    getValues,
    control,
    watch,
    formState: { isValid, touchedFields, dirtyFields },
  } = methods;

  const onSaveClick = async (): Promise<void> => {
    const validated = await trigger();

    if (!validated) {
      return;
    }

    const valueSnapshot = getValues();

    Log.debug(
      `Set value for availability item target field:  ${fieldName}`,
      valueSnapshot,
    );
    setValueOuter?.(fieldName, valueSnapshot, {
      shouldValidate: true,
      shouldDirty: true,
    });
    onCancelOuter?.();
  };

  const availabilityDayTypeIds = watch("dayTypeIds");
  const availabilityDayGroupIds = watch("dayGroupIds");
  const availabilityDateFrom = watch("from");
  const availabilityTo = watch("to");

  useEffect(() => {
    for (const dependantFieldName of availabilityFormFieldNames) {
      Log.debug(
        "Trigger dependant controls validation for ",
        dependantFieldName,
        availabilityDayTypeIds,
        availabilityDayGroupIds,
        availabilityDateFrom,
        availabilityTo,
      );

      if (
        touchedFields[dependantFieldName] ||
        dirtyFields[dependantFieldName]
      ) {
        trigger(dependantFieldName);
      }
    }
  }, [
    trigger,
    availabilityDayTypeIds,
    availabilityDayGroupIds,
    touchedFields,
    availabilityDateFrom,
    availabilityTo,
    dirtyFields,
  ]);

  const trafficTypes = useMemo(
    () => [
      { value: TrafficTypeEnum.Low, label: t("selectListLabel.low") },
      { value: TrafficTypeEnum.Normal, label: t("selectListLabel.normal") },
      { value: TrafficTypeEnum.Peak, label: t("selectListLabel.peak") },
    ],
    [t],
  );

  return (
    <FormProvider {...methods}>
      <div
        className="availability-item no-blur-item"
        style={innerItemEditStyles}
      >
        <div className="availability-item__editors">
          <MultiSelectBoundInputByControl
            name={`dayGroupIds`}
            control={control}
            formLabel={capitalize(t("dayGroup.plural"))}
            formAttributes={{
              dropItemsSource: "dayGroup",
            }}
          />

          <MultiSelectBoundInputByControl
            name={`dayTypeIds`}
            control={control}
            formLabel={capitalize(t("dayType.plural"))}
            formAttributes={{
              dropItemsSource: "dayType",
            }}
          />

          <TimeTemplate
            timeFormat={timeFormat}
            name="from"
            labelTranslated={t(`general.FROM`)}
            formValidation={requiredRuleStringField}
          />
          <TimeTemplate
            timeFormat={timeFormat}
            name="to"
            labelTranslated={t(`general.TO`)}
            formValidation={requiredRuleStringField}
          />
          <DropdownInputByControl
            name={`traffic`}
            control={control}
            formLabel={t("systemParam.formLabel.traffic")}
            formAttributes={{
              selectItems: trafficTypes,
            }}
          />
        </div>
        <div className="availability-item__buttons">
          <ColorIconButton
            disabled={!isValid}
            iconType="CHECK"
            edge="end"
            aria-label="save"
            onClick={onSaveClick}
          />

          <ColorIconButton
            iconType="CANCEL"
            edge="end"
            aria-label="cancel"
            onClick={() => {
              onCancelOuter?.();
            }}
          />
        </div>
      </div>
    </FormProvider>
  );
};
